import * as React from 'react';

import Alert from '@mui/material/Alert';
import AuthImage from '../components/AuthImage';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { getClockRequests, reviewClockRequest } from '../lib/admin';
import { requestPhotoPath } from '../lib/clockLocations';

const MAX_NOTE_LENGTH = 500;

// One pending request, with its photos and details, and the buttons to approve or
// reject it. The note is optional and is shown to the person who asked.
function ClockRequestCard({ clock, onReviewed }) {
    const [note, setNote] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');

    const review = async (status) => {
        setBusy(true);
        setError('');
        const response = await reviewClockRequest(clock.id, status, note);
        setBusy(false);
        if (response.ok) {
            onReviewed(clock, response.clock);
        } else if (response.status === 409) {
            // someone else already dealt with it: drop it from the list
            onReviewed(clock, null, response.message);
        } else {
            setError(response.message);
        }
    };

    const details = [
        ['Address', clock.address],
        ['Ward', clock.ward],
        ['Visibility', clock.visibility],
        ['Clock type', clock.clock_type],
        ['Surveyed by', clock.surveyed_by],
        ['Date of last survey', clock.survey_date],
    ].filter(([, value]) => value);

    return (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle1">{clock.name}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
                Requested by {clock.submitted_by || 'unknown'}{clock.submitted_by_email ? ` (${clock.submitted_by_email})` : ''}
            </Typography>
            {clock.photo_ids && clock.photo_ids.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {clock.photo_ids.map((id, index) => (
                        <AuthImage key={id} path={requestPhotoPath(id)} alt={`${clock.name} photo ${index + 1}`} size={96} />
                    ))}
                </Box>
            ) : null}
            <Box component="dl" sx={{ m: 0, mt: 1, fontSize: 14 }}>
                {details.map(([label, value]) => (
                    <Box key={label} sx={{ display: 'flex', gap: 1 }}>
                        <Box component="dt" sx={{ color: 'text.secondary', minWidth: 130 }}>{label}</Box>
                        <Box component="dd" sx={{ m: 0 }}>{value}</Box>
                    </Box>
                ))}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box component="dt" sx={{ color: 'text.secondary', minWidth: 130 }}>Location</Box>
                    <Box component="dd" sx={{ m: 0 }}>
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`https://www.google.com/maps?q=${clock.lat},${clock.lon}`}
                        >
                            {clock.lat.toFixed(6)}, {clock.lon.toFixed(6)}
                        </a>
                    </Box>
                </Box>
            </Box>
            <TextField
                label="Note to the requester (optional)"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                disabled={busy}
                fullWidth
                multiline
                maxRows={4}
                size="small"
                sx={{ mt: 2 }}
                inputProps={{ maxLength: MAX_NOTE_LENGTH }}
            />
            {error ? <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert> : null}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button variant="contained" onClick={() => review('approved')} disabled={busy}>Approve</Button>
                <Button variant="outlined" color="error" onClick={() => review('rejected')} disabled={busy}>Reject</Button>
            </Box>
        </Paper>
    );
}

// Admin: the clock requests waiting for a decision.
export default function AdminClockReview({ onReviewed }) {
    const [state, setState] = React.useState({ loading: true, clocks: [], error: '' });
    const [message, setMessage] = React.useState(null);

    React.useEffect(() => {
        let cancelled = false;
        getClockRequests('pending').then((response) => {
            if (cancelled) return;
            setState(response.ok
                ? { loading: false, clocks: response.clocks, error: '' }
                : { loading: false, clocks: [], error: response.message });
        });
        return () => { cancelled = true; };
    }, []);

    const handleReviewed = (clock, reviewed, conflict) => {
        setState((old) => ({ ...old, clocks: old.clocks.filter((c) => c.id !== clock.id) }));
        if (conflict) {
            setMessage({ severity: 'info', text: conflict });
        } else {
            setMessage({
                severity: 'success',
                text: reviewed.status === 'approved'
                    ? `${clock.name} is now live on the map.`
                    : `${clock.name} was not approved.`,
            });
        }
        if (onReviewed) onReviewed();
    };

    return (
        <Box>
            <Typography variant="subtitle1">Clock requests waiting for approval</Typography>
            {message ? <Alert severity={message.severity} sx={{ mt: 1 }} onClose={() => setMessage(null)}>{message.text}</Alert> : null}
            {state.loading ? <Box sx={{ textAlign: 'center', p: 2 }}><CircularProgress size={24} /></Box> : null}
            {state.error ? <Alert severity="error" sx={{ mt: 1 }}>{state.error}</Alert> : null}
            {!state.loading && !state.error && state.clocks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Nothing is waiting for approval.</Typography>
            ) : null}
            {state.clocks.map((clock) => (
                <ClockRequestCard key={clock.id} clock={clock} onReviewed={handleReviewed} />
            ))}
        </Box>
    );
}
